import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCobranding } from '../../utilities/cobranding/cobranding';
import { useAnalyticsClient } from '../../utilities/analytics/analytics-web-client';
import { AdvancedCobrandedPage } from './models';
import { AdvancedCobrandingContext } from './AdvancedCobrandingContext';
import { PageGuard } from './PageGuard';
import ErrorBoundary from '../../components/ErrorBoundary';

type PageOrchestratorProps = {
  fallback: React.FunctionComponent;
  pages: Array<AdvancedCobrandedPage>;
  requiredComponents: Array<string>;
};

export const PageOrchestrator = ({
  fallback,
  pages,
  requiredComponents,
}: PageOrchestratorProps) => {
  const [registrationStarted, setRegistrationStarted] = useState(false);
  const [components, setComponents] = useState<Array<string>>();
  const { cobranding } = useCobranding();
  const analyticsClient = useAnalyticsClient();

  const page = useMemo(() => {
    return pages.find(page => page.cobranding === cobranding);
  }, [pages, cobranding]);

  const Component = useMemo(() => {
    return page?.Component || fallback;
  }, [page, fallback]);

  // The fallback is the base page. This occurs when there is a cobranding defined, but not for the page that the user is requesting
  const isUsingFallback = !page?.Component;

  const registerComponent = useCallback((component: string) => {
    setComponents(components => {
      return components ? [...components, component] : [component];
    });
  }, []);

  const value = useMemo(() => ({ components, registerComponent }), [components, registerComponent]);

  useEffect(() => {
    setTimeout(() => setRegistrationStarted(true), 0);
  }, []);

  return (
    <AdvancedCobrandingContext.Provider value={value}>
      <PageGuard
        requiredComponents={requiredComponents}
        registrationStarted={registrationStarted}
        skipRequiredChecks={isUsingFallback}>
        {/* TODO(advanced-cobranding): Take in page.onFail and call it if the page cannot be rendered */}
        <ErrorBoundary
          analyticsClient={analyticsClient}
          capabilities={['non-kirby-code']}
          skipAnalytics // page failure analytics feed into Kirby SLOs – we do not want to be accountable for cobranding experiences failing
        >
          {/* TODO(advanced-cobranding): Need to add a Suspense Boundary */}
          <Component />
        </ErrorBoundary>
      </PageGuard>
    </AdvancedCobrandingContext.Provider>
  );
};
